import { mapActions, mapGetters, mapMutations } from 'vuex'
import { funnel, types, intents } from '@/utils/clusterVariables.js'

export default {
  name: 'Ideation',
  data () {
    return {
      isLoading: {
        articles: false,
        writing: false,
        changeTopic: false,
        changeFunnel: false,
        changeIntent: false,
        changeType: false,
        addNewCluster: false,
        keywordIdeas: false
      },
      keyword: '',
      lastSubmittedPrompt: '',
      existingArticles: [],
      existingKeywords: [],
      displayWritePopup: false,
      selectedArticle: null,
      selectedFunnel: null,
      selectedIntent: null,
      selectedType: null,
      selectedTopic: null,
      keywordSuggestionTopicId: null,
      funnel,
      types,
      intents,
      keywordIdeas: [],
      openPanels: [0] // Default open the first panel
    }
  },
  watch: {
    async $route () {
      await this.loadData()
    }
  },
  computed: {
    ...mapGetters([
      'activeWorkspace',
      'activeContentplan',
      'articles',
      'topics',
      'websocketConnected' // Add the websocket connection status
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan ? this.activeContentplan.name : ''
    },
    remainingCredits () {
      return this.activeWorkspace?.remaining_article_credits
    },
    topicSelection () {
      return this.topics?.map(topic => ({
        text: topic.name,
        value: topic.id
      })) ?? []
    },
    showNewClusterCard () {
      const articleTitles = this.existingArticles?.map(article => article.title)
      return this.lastSubmittedPrompt && !this.isLoading.articles && !articleTitles.includes(this.lastSubmittedPrompt)
    }
  },
  methods: {
    ...mapActions([
      'articleGet',
      'topicGetList',
      'articlePut',
      'articlePost',
      'postHubExpand',
      'sendWebsocketMessage'
    ]),
    ...mapMutations([
      'addProgressTracker',
      'updateProgressTracker',
      'setSnackbar'
    ]),
    submitKeyword (topicId = null) {
      if (!this.keyword || !this.keyword.trim()) {
        this.setSnackbar({ open: true, text: 'Please enter a keyword first', type: 'error' })
        return
      }
      this.keyword = this.keyword.trim()
      this.keywordSuggestionTopicId = topicId
      this.openWritePopup()
    },
    selectKeyword (keyword, topicId) {
      this.keyword = keyword
      this.submitKeyword(topicId)
    },
    async loadData () {
      document.title = 'New article | ContentGecko'
      this.isLoading.keywordIdeas = true
      try {
        const result = await Promise.all([
          this.topicGetList({
            contentplanId: this.contentplanId
          }),
          this.postHubExpand({
            contentplanId: this.contentplanId,
            runRelatedKeywords: false,
            saveKeywords: false
          })
        ])
        this.keywordIdeas = result[1]
      } catch (error) {
        console.error('Error loading keyword ideas:', error)
      } finally {
        this.isLoading.keywordIdeas = false
      }
    },
    async getClusters () {
      this.lastSubmittedPrompt = this.keyword
      this.isLoading.articles = true
      this.existingArticles = []
      this.existingKeywords = []

      try {
        const [exactTitleMatches, titleMatches, keywordMatches] = await Promise.all([
          // Search for exact title matches
          this.articleGet({
            contentplanId: this.contentplanId,
            filters: [
              { field: 'contentplanId', op: 'eq', value: this.contentplanId },
              { field: 'title', op: 'regexp', value: `^${this.lastSubmittedPrompt}$` }
            ],
            sort: [{ field: 'estimatedSearchVolume', direction: 'desc' }],
            pagination: { limit: 10, offset: 0 },
            includeKeywords: true,
            straightReturn: true
          }),
          // Search for partial title matches
          this.articleGet({
            contentplanId: this.contentplanId,
            filters: [
              { field: 'contentplanId', op: 'eq', value: this.contentplanId },
              { field: 'title', op: 'regexp', value: this.lastSubmittedPrompt }
            ],
            sort: [{ field: 'estimatedSearchVolume', direction: 'desc' }],
            pagination: { limit: 10, offset: 0 },
            includeKeywords: true,
            straightReturn: true
          }),
          // Search for keyword matches
          this.articleGet({
            contentplanId: this.contentplanId,
            filters: [
              { field: 'contentplanId', op: 'eq', value: this.contentplanId },
              { field: 'keyword', op: 'regexp', value: this.lastSubmittedPrompt }
            ],
            sort: [{ field: 'estimatedSearchVolume', direction: 'desc' }],
            pagination: { limit: 10, offset: 0 },
            includeKeywords: true,
            straightReturn: true
          })
        ])
        await this.calcExistingArticles(exactTitleMatches.items, titleMatches.items, keywordMatches.items)
        await this.calcExistingKeywords(exactTitleMatches.keywords, titleMatches.keywords, keywordMatches.keywords)
      } catch (error) {
        console.error(error)
      }
      this.isLoading.articles = false
    },
    async calcExistingArticles (exactTitleMatchArticles, titleMatchArticles, keywordMatchArticles) {
      // Remove duplicates by article_id and prioritize exact matches, then partial title matches, then keyword matches
      if (!exactTitleMatchArticles && !titleMatchArticles && !keywordMatchArticles) {
        this.existingArticles = []
        return
      }

      const seenArticleIds = new Set()

      // Add exact title matches first
      if (exactTitleMatchArticles) {
        exactTitleMatchArticles.forEach(article => {
          if (!seenArticleIds.has(article.article_id)) {
            this.existingArticles.push({
              ...article,
              exact_match: true
            })
            seenArticleIds.add(article.article_id)
          }
        })
      }

      // Add partial title matches second
      if (titleMatchArticles) {
        titleMatchArticles.forEach(article => {
          if (!seenArticleIds.has(article.article_id)) {
            this.existingArticles.push({
              ...article,
              exact_match: false
            })
            seenArticleIds.add(article.article_id)
          }
        })
      }

      // Then add keyword matches
      if (keywordMatchArticles) {
        keywordMatchArticles.forEach(article => {
          if (!seenArticleIds.has(article.article_id)) {
            this.existingArticles.push({
              ...article,
              exact_match: false
            })
            seenArticleIds.add(article.article_id)
          }
        })
      }
      console.log('existingArticles', this.existingArticles?.length)
    },
    async calcExistingKeywords (exactTitleMatchKeywords, titleMatchKeywords, keywordMatchKeywords) {
      if (!exactTitleMatchKeywords && !titleMatchKeywords && !keywordMatchKeywords) {
        this.existingKeywords = []
        return
      }
      const seenKeywordIds = new Set()

      // Add exact title matches first
      if (exactTitleMatchKeywords) {
        exactTitleMatchKeywords.forEach(keyword => {
          if (!seenKeywordIds.has(keyword.keyword_id)) {
            this.existingKeywords.push(keyword)
            seenKeywordIds.add(keyword.keyword_id)
          }
        })
      }

      // Add partial title matches second
      if (titleMatchKeywords) {
        titleMatchKeywords.forEach(keyword => {
          if (!seenKeywordIds.has(keyword.keyword_id)) {
            this.existingKeywords.push(keyword)
            seenKeywordIds.add(keyword.keyword_id)
          }
        })
      }

      // Then add keyword matches
      if (keywordMatchKeywords) {
        keywordMatchKeywords.forEach(keyword => {
          if (!seenKeywordIds.has(keyword.keyword_id)) {
            this.existingKeywords.push(keyword)
            seenKeywordIds.add(keyword.keyword_id)
          }
        })
      }
      console.log('existingKeywords', this.existingKeywords?.length)
    },
    getArticleKeywords (articleId) {
      if (!this.existingKeywords?.length) return []
      return this.existingKeywords.filter(k => k.article_id === articleId)
    },
    topicIdToName (topicId) {
      const topic = this.topics.find(t => t.id === topicId)
      return topic ? topic.name : null
    },
    openWritePopup () {
      this.lastSubmittedPrompt = ''
      this.existingArticles = []
      this.existingKeywords = []
      this.selectedArticle = null
      this.selectedTopic = this.keywordSuggestionTopicId
      this.selectedFunnel = null
      this.selectedIntent = null
      this.selectedType = null
      this.displayWritePopup = true
    },
    closeWritePopup () {
      this.keyword = ''
      this.selectedArticle = null
      this.selectedTopic = null
      this.selectedFunnel = null
      this.selectedIntent = null
      this.selectedType = null
      this.displayWritePopup = false
    },
    async changeTopic (articleId, topicId) {
      if (!articleId || !topicId) return
      this.isLoading.changeTopic = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateTopic',
          articleIds: [articleId],
          topicId
        })
      } catch (e) {
        console.log(e)
      }

      this.isLoading.changeTopic = false
    },
    async changeFunnel (articleId, funnel) {
      if (!articleId || !funnel) return
      this.isLoading.changeFunnel = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateFunnel',
          articleIds: articleId,
          funnel
        })
      } catch (e) {
        console.log(e)
      }

      this.isLoading.changeFunnel = false
    },
    async changeIntent (articleId, intent) {
      if (!articleId || !intent) return
      this.isLoading.changeIntent = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateIntent',
          articleIds: articleId,
          intent
        })
      } catch (e) {
        console.log(e)
      }

      this.isLoading.changeIntent = false
    },
    async changeType (articleId, type) {
      if (!articleId || !type) return
      this.isLoading.changeType = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateType',
          articleIds: articleId,
          type
        })
      } catch (e) {
        console.log(e)
      }

      this.isLoading.changeType = false
    },
    async writeArticle () {
      if (this.activeWorkspace && this.activeWorkspace.remaining_article_credits <= 0) {
        this.setSnackbar({ open: true, text: 'You have no article credits remaining. Upgrade your plan to produce more articles.', type: 'error' })
        return
      }

      if (!this.selectedTopic) {
        this.setSnackbar({ open: true, text: 'Please select a topic before confirming', type: 'error' })
        return
      }

      if (!this.keyword || !this.keyword.trim()) {
        this.setSnackbar({ open: true, text: 'Please enter a keyword before writing', type: 'error' })
        return
      }

      this.isLoading.writing = true
      const articleTitle = this.keyword.trim()
      const topicId = Number(this.selectedTopic)
      let articleId = null

      if (!Number.isInteger(topicId) || topicId <= 0) {
        this.setSnackbar({ open: true, text: 'Please select a valid topic before confirming', type: 'error' })
        this.isLoading.writing = false
        return
      }

      try {
        const postPayload = {
          contentplanId: this.contentplanId,
          topicId,
          favorite: true,
          name: articleTitle
        }
        if (this.selectedFunnel && this.selectedIntent && this.selectedType) {
          postPayload.funnel = this.selectedFunnel
          postPayload.intent = this.selectedIntent
          postPayload.type = this.selectedType
        }
        const newArticle = await this.articlePost(postPayload)
        articleId = newArticle.articleId
        if (!articleId) throw new Error('articleId missing in articlePost response')

        const updateRequests = []
        const labelsOnCreate = !!(this.selectedFunnel && this.selectedIntent && this.selectedType)
        if (!labelsOnCreate && this.selectedFunnel) {
          updateRequests.push(this.articlePut({
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId,
            action: 'updateFunnel',
            articleIds: [articleId],
            funnel: this.selectedFunnel
          }))
        }
        if (!labelsOnCreate && this.selectedIntent) {
          updateRequests.push(this.articlePut({
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId,
            action: 'updateIntent',
            articleIds: [articleId],
            intent: this.selectedIntent
          }))
        }
        if (!labelsOnCreate && this.selectedType) {
          updateRequests.push(this.articlePut({
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId,
            action: 'updateType',
            articleIds: [articleId],
            type: this.selectedType
          }))
        }
        if (updateRequests.length) await Promise.all(updateRequests)

        this.addProgressTracker({
          contentplanId: this.contentplanId,
          clusterId: articleId,
          name: articleTitle,
          stepsObject: {
            Briefing: 'inProgress',
            Researching: 'pending',
            Drafting: 'pending',
            Editing: 'pending',
            Illustrating: 'pending'
          }
        })

        await Promise.all([
          this.articlePut({
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId,
            action: 'addToMap',
            articleIds: [articleId]
          }),
          this.sendWebsocketMessage({
            action: 'runOrchestrator',
            message: {
              contentplanId: this.contentplanId,
              clusterIds: [articleId],
              runWriting: true,
              selectBy: 'cluster',
              publishTo: this.activeContentplan.main_cms_integration || 'autoblog',
              userType: 'managed'
            }
          })
        ])

        this.closeWritePopup()
      } catch (error) {
        console.log(error)
        // Update the tracker to show an error
        if (articleId) {
          this.updateProgressTracker({
            id: articleId,
            stepName: 'Brief',
            status: 'error'
          })
        }
      }

      this.isLoading.writing = false
    },
    async addNewCluster () {
      if (this.activeWorkspace && this.activeWorkspace.remaining_article_credits <= 0) {
        this.setSnackbar({ open: true, text: 'You have no article credits remaining. Upgrade your plan to produce more articles.', type: 'error' })
        return
      }
      this.isLoading.addNewCluster = true
      try {
        const articlePostData = {
          contentplanId: this.contentplanId,
          topicId: this.keywordSuggestionTopicId,
          favorite: true,
          name: this.keyword
        }

        const newArticle = await this.articlePost(articlePostData)
        this.openWritePopup(newArticle)
      } catch (error) {
        console.log(error)
      }
      this.isLoading.addNewCluster = false
    }
  },
  async mounted () {
    await this.loadData()
  }
}
